import { Grid, Paper, Box, TextField } from "@mui/material";
import {
  LinearProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ReadOnlyTextField from "../../ui/ReadOnlyTextField";
import { store } from "../../../store";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import { getOrder, updateOrder } from "../../../redux/actions/order";
import OrderTable from "../../ui/OrderTable";
import dayjs from "dayjs";
import UserInfo from "../../ui/UserInfo";
import "jspdf-autotable";
import {
  getShipmentById,
  updateShipmentComment,
  updateShipmentDelivery,
  updateShipmentStatus,
  updateShipment,
} from "../../../services/shipmentService";
import {
  fetchProductionsByOrderId,
  updateProductionStatus,
} from "../../../services/productionService";
import { updateOrderStatus } from "../../../services/orderService";
import {
  showErrorAlert,
  showSuccessAlert,
  showWarningAlert,
} from "../../ui/utils/AlertUtils";
import {
  printCdlTracking,
  printShippingLabel,
  printPackingList,
  printAorForm,
} from "../../ui/PrintFunctions";
import EditShippingAddressDialog from "../../ui/dialogs/EditShippingAddressDialog";
import PackageDetails from "../../ui/PackageDetails";
import ReadOnlyField from "../../ui/ReadOnlyField";
import { useTranslation } from "react-i18next";
import AddressContactInfo from "../../ui/AddressContactInfo";
import EditFieldDialog from "../../ui/dialogs/EditFieldDialog";
import CustomDatePicker from "../../ui/utils/CustomDatePicker";
import OrderPageHeader from "./order/shared/OrderPageHeader";
import { BackorderControl } from "../../ui/BackorderControl";
import {
  MaterialOrderGridGroup,
  materialOrderGridListSx,
} from "../../ui/MaterialOrderGridLayout";

// ─── Shipment Info Screen ──────────────────────────────────────────────────────
// ─── Imports ───────────────────────────────────────────────────────────────────

export const ScreenShippingInfo = (props) => {
  // ─── Hooks, Routing, Selectors & Local State ─────────────────────────────────
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const order = useSelector((state) => state.order.order);
  const orderId = location.state?.orderId;
  const studyId = location.state?.studyId;
  const centerId = location.state?.centerId;
  const [isLoading, setIsLoading] = React.useState(true);
  const user = useSelector((state) => state.user.user);
  const cdlUser = user.organization === "CDL";
  const centerNumber = location.state?.centerNumber;
  const studyCode = location.state?.studyCode;
  const shipmentIdFromState = Number(location.state?.shipmentId);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [shipment, setShipment] = React.useState(null);
  const [shippedProductions, setShippedProductions] = React.useState([]);
  const [shippedProductionsLoaded, setShippedProductionsLoaded] =
    React.useState(false);
  const [comment, setComment] = React.useState("");
  const [onBackorder, setOnBackorder] = React.useState(false);
  const [packages, setPackages] = React.useState([]);
  const [deliveryDate, setDeliveryDate] = React.useState(
    new Date().toISOString().split("T")[0],
  );
  // Add local state for editable shipping info fields
  const [shippingInfo, setShippingInfo] = React.useState({
    preparationDate: "",
    sendingDate: "",
    carrierOrderDate: "",
    carrier: "",
    packingSlip: "",
  });
  const [open, setOpen] = React.useState(false);
  const [currentField, setCurrentField] = React.useState("");
  const [currentValue, setCurrentValue] = React.useState("");
  const [currentLabel, setCurrentLabel] = React.useState("");
  const study = useSelector((state) =>
    state.study.studies.find((study) => study.cdlStudyCode === studyCode),
  );
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [hospital, setHospital] = React.useState(order?.hospital || "");
  const [ward, setWard] = React.useState(order?.ward || "");
  const [building, setBuilding] = React.useState(order?.building || "");
  const [street, setStreet] = React.useState(order?.street || "");
  const [floor, setFloor] = React.useState(order?.floor || "");
  const [zipcode, setZipcode] = React.useState(order?.zipcode || "");
  const [shippingAddress, setShippingAddress] = React.useState(
    order?.shippingAddress || "",
  );
  const [updateStatus, setUpdateStatus] = React.useState(false);
  const fullAddress = AddressContactInfo({ order });
  // Add a refresh key for OrderTable
  const [orderRefreshKey, setOrderRefreshKey] = React.useState(0);
  const handleBackToOrder = () => {
    navigate("/order", {
      state: {
        orderId,
        order,
        centerNumber,
        studyCode,
        studyId: study?.id || studyId,
        centerId,
      },
    });
  };

  const shipmentId = React.useMemo(() => {
    if (!Number.isNaN(shipmentIdFromState) && shipmentIdFromState > 0) {
      return shipmentIdFromState;
    }

    return null;
  }, [shipmentIdFromState]);

  // ─── Address Dialog Handlers ─────────────────────────────────────────────────
  const handleDoubleClickAddress = () => {
    setDialogOpen(true);
  };
  const handleClose = () => {
    setDialogOpen(false);
  };

  // ─── Inline Edit (Double-Click) Handlers ─────────────────────────────────────
  const handleDoubleClick = (field, label, value) => {
    if (cdlUser) {
      setCurrentField(field);
      setCurrentLabel(label);
      setCurrentValue(value);
      setOpen(true);
    }
  };

  // ─── Save Shipping Address ───────────────────────────────────────────────────
  const handleSaveAddress = async () => {
    if (
      hospital.trim() === "" ||
      street.trim() === "" ||
      zipcode.trim() === "" ||
      shippingAddress.trim() === ""
    ) {
      setErrorMessage(t("addressError"));
      return;
    }
    const updatedOrder = {
      hospital: hospital.trim() !== "" ? hospital : null,
      ward: ward.trim() !== "" ? ward : null,
      building: building.trim() !== "" ? building : null,
      street,
      floor,
      zipcode,
      shippingAddress,
    };

    store.dispatch(updateOrder(order.id, updatedOrder));
    setUpdateStatus((prevStatus) => !prevStatus);
    setErrorMessage("");
    setDialogOpen(false);
    showSuccessAlert(t("orderAddressUpdated"));
  };

  // Helper to refresh order (Redux) and current shipment
  const refreshOrderAndShipment = async () => {
    await store.dispatch(getOrder(orderId));
    await fetchShipment();
    // Force OrderTable to remount with latest order data
    setOrderRefreshKey((k) => k + 1);
  };

  // ─── Save Editable Shipment Field ────────────────────────────────────────────
  const handleSave = async (field, newValue) => {
    if (shipment && shipment.id) {
      await updateShipment(shipment.id, field, newValue);
    }
    setUpdateStatus((prevStatus) => !prevStatus);
    showSuccessAlert(t("shipmentUpdated"));

    // Refresh both order and shipment so header and details update
    await refreshOrderAndShipment();
  };

  // Bulk update for all shipping info + comment
  const handleUpdateAll = async () => {
    if (!shipment) return;
    const updates = [];
    const current = {
      preparationDate: shipment?.preparationDate?.split("T")[0] || "",
      sendingDate: shipment?.sendingDate?.split("T")[0] || "",
      carrierOrderDate: shipment?.carrierOrderDate?.split("T")[0] || "",
      carrier: shipment?.carrier || "",
      packingSlip: shipment?.packingSlip || "",
      comment: shipment?.comment || "",
      onBackorder: Boolean(shipment?.onBackorder),
    };

    if (shippingInfo.preparationDate !== current.preparationDate) {
      updates.push(
        updateShipment(
          shipment.id,
          "preparationDate",
          shippingInfo.preparationDate,
        ),
      );
    }
    if (shippingInfo.sendingDate !== current.sendingDate) {
      updates.push(
        updateShipment(shipment.id, "sendingDate", shippingInfo.sendingDate),
      );
    }
    if (shippingInfo.carrierOrderDate !== current.carrierOrderDate) {
      updates.push(
        updateShipment(
          shipment.id,
          "carrierOrderDate",
          shippingInfo.carrierOrderDate,
        ),
      );
    }
    if (shippingInfo.carrier !== current.carrier) {
      updates.push(
        updateShipment(shipment.id, "carrier", shippingInfo.carrier),
      );
    }
    if (shippingInfo.packingSlip !== current.packingSlip) {
      updates.push(
        updateShipment(shipment.id, "packingSlip", shippingInfo.packingSlip),
      );
    }
    if (comment !== current.comment) {
      updates.push(updateShipmentComment(shipment.id, comment));
    }
    if (onBackorder !== current.onBackorder) {
      updates.push(updateShipment(shipment.id, "onBackorder", onBackorder));
    }

    // Format package details string and update if changed
    const formatPackageDetails = (pkgs) => {
      if (!pkgs || pkgs.length === 0) return "";
      let details = `${pkgs.length} nb of pkg, `;
      details += pkgs
        .map(
          (p, i) =>
            `pkg ${i + 1}: ${parseInt(p.length) || 0} l, ${
              parseInt(p.height) || 0
            } h, ${parseInt(p.width) || 0} w, ${
              parseFloat(p.volumetricWeight) || 0
            } vw`,
        )
        .join("; ");
      return details;
    };
    const newDetails = formatPackageDetails(packages);
    const currentDetails = shipment?.packageDetails || "";
    if (newDetails && newDetails !== currentDetails) {
      updates.push(updateShipment(shipment.id, "packageDetails", newDetails));
    }

    if (updates.length === 0) {
      showSuccessAlert(t("shipmentUpdated"));
      return;
    }

    await Promise.all(updates);
    setUpdateStatus((prev) => !prev);
    showSuccessAlert(t("shipmentUpdated"));

    // Refresh both order and shipment so header and details update
    await refreshOrderAndShipment();
  };

  // ─── Fetch Shipment & Parse Package Details ──────────────────────────────────
  const fetchShipment = async () => {
    if (shipmentId) {
      const fetchedShipment = await getShipmentById(shipmentId);
      setShipment(fetchedShipment);

      if (fetchedShipment && fetchedShipment.packageDetails) {
        // Extract the package details
        const packageRegex =
          /pkg\s+\d+:\s*(\d+(?:\.\d+)?)\s*l,\s*(\d+(?:\.\d+)?)\s*h,\s*(\d+(?:\.\d+)?)\s*w,\s*(\d+(?:\.\d+)?)\s*vw/gi;
        const matches = Array.from(
          fetchedShipment.packageDetails.matchAll(packageRegex),
        );

        const parsedPackages = matches.map((match) => ({
          length: Number(match[1]) || 0,
          height: Number(match[2]) || 0,
          width: Number(match[3]) || 0,
          volumetricWeight: Number(match[4]) || 0,
        }));

        setPackages(parsedPackages);
      } else {
        setPackages([]);
      }
    } else {
      console.error("Invalid shipmentId for shipment details screen");
    }
  };

  React.useEffect(() => {
    fetchShipment();
    // eslint-disable-next-line
  }, [shipmentId, updateStatus]);

  // ─── Shipment Actions ────────────────────────────────────────────────────────
  const handleLostShipment = async () => {
    const result = await showWarningAlert(t("markShipmentAsLostWarning"));

    if (result.isConfirmed) {
      try {
        await updateShipmentStatus(
          shipment,
          "lost",
          "Shipment lost - " + new Date().toLocaleDateString(),
        );

        // Update productions status to 'lost'
        const updateProductions = shipment.productions?.map((production) =>
          updateProductionStatus(
            production.id,
            "lost",
            null,
            null,
            "Lost as shipment lost on " + new Date().toLocaleDateString(),
          ),
        );
        await Promise.all(updateProductions);

        await showSuccessAlert(t("shipmentMarkedAsLost"));

        navigate("/order", {
          state: { orderId, order, centerNumber, studyCode },
        });
      } catch (error) {
        showErrorAlert(t("errorOccured"));
      }
    }
  };

  const confirmDelivery = async () => {
    const result = await showWarningAlert(t("confirmDeliveryWarning"));

    if (result.isConfirmed) {
      try {
        await updateShipmentDelivery(shipment.id, "delivered", deliveryDate);

        // Update productions status to 'at center'
        const updateProductions = shipment.productions?.map((production) =>
          updateProductionStatus(production.id, "at center"),
        );
        await Promise.all(updateProductions);

        // Update the order status
        let orderStatus = "delivered 1/2";
        const totalShippedQuantity = order.shipments.reduce(
          (sum, shipment) => sum + shipment.shippedQuantity,
          0,
        );

        if (totalShippedQuantity === order.totalQuantity) {
          orderStatus = "delivered";
        }

        await updateOrderStatus(order.id, orderStatus);
        showSuccessAlert("The shipment has been confirmed as delivered");

        navigate("/order", {
          state: {
            orderId,
            order,
            centerNumber,
            studyCode,
            studyId: study.id,
            centerId,
          },
        });
      } catch (error) {
        console.error(error);
        showErrorAlert(t("errorOccured"));
      }
    }
  };

  const handleAOR = () => {
    const targetShipmentId = shipmentId || shipment?.id;
    if (!targetShipmentId) {
      showErrorAlert(t("errorOccured"));
      return;
    }

    navigate("/aor-form", {
      state: {
        orderId,
        order,
        centerNumber,
        studyCode,
        shipmentId: targetShipmentId,
        centerId,
        studyId: study.id,
      },
    });
  };

  const seeAOR = () => {
    const targetShipmentId = shipmentId || shipment?.id;

    navigate("/aor", {
      state: {
        orderId,
        order,
        centerNumber,
        studyCode,
        shipmentId: targetShipmentId,
        centerId,
        studyId: study.id,
      },
    });
  };

  const loadShippedProductions = React.useCallback(async () => {
    if (!orderId) {
      setShippedProductions([]);
      setShippedProductionsLoaded(true);
      return [];
    }

    setShippedProductionsLoaded(false);

    try {
      const productions = await fetchProductionsByOrderId(orderId);
      const normalizedProductions = Array.isArray(productions)
        ? productions
        : [];
      setShippedProductions(normalizedProductions);
      setShippedProductionsLoaded(true);
      return normalizedProductions;
    } catch (error) {
      console.error("Error loading shipped productions:", error);
      setShippedProductions([]);
      setShippedProductionsLoaded(true);
      return [];
    }
  }, [orderId]);

  const handlePrintAorForm = async () => {
    const productions = shippedProductionsLoaded
      ? shippedProductions
      : await loadShippedProductions();

    printAorForm(order, shipment, centerNumber, study, productions);
  };

  const materialsTypeMaterial = order?.Supplies?.filter(
    (supply) => supply.Material.type === "material",
  );
  const materialsTypeSupply = order?.Supplies?.filter(
    (supply) => supply.Material.type === "supply",
  );
  const materialsTypeReagent = order?.Supplies?.filter(
    (supply) => supply.Material.type === "reagent",
  );
  const materialsTypeDocuments = order?.Supplies?.filter(
    (supply) => supply.Material.type === "document",
  );

  const CustomDataGrid = ({ rows, headerName }) => {
    const columns = [
      {
        field: "materialName",
        headerName: headerName,
        flex: 0.3,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.Material.name,
      },
      {
        field: "shippingQuantity",
        headerName: t("sentQuantity"),
        flex: 0.1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const productionsWithSameMaterial =
            shipment?.productions?.filter(
              (production) => production.materialId === params.row.Material.id,
            ) || [];

          return <div>{productionsWithSameMaterial.length}</div>;
        },
      },
      {
        field: "productionsSent",
        headerName: t("selectedProductions"),
        flex: 0.3,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (params) => {
          const productionsWithSameMaterial =
            shipment?.productions.filter(
              (production) => production.materialId === params.row.Material.id,
            ) || [];
          const references = productionsWithSameMaterial?.map((production) => {
            return `(${production.batchNumber}) ${production.reference} ${
              production.Protocols && production.Protocols.length > 0
                ? `[${production.Protocols.map((p) => p.reference).join(
                    ", ",
                  )} ${
                    production.cdlComment ? " - " + production.cdlComment : ""
                  }]`
                : production.cdlComment
                  ? `[${production.cdlComment}]`
                  : ""
            } `;
          });
          return references.join(", ");
        },
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
            {params.value}
          </div>
        ),
      },
    ];

    if (!rows || rows.length === 0) {
      return null;
    }

    return (
      <MaterialOrderGridGroup>
        <DataGrid
          slots={{
            noRowsOverlay: NoRowOverlay,
            loadingOverlay: LinearProgress,
          }}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 30 },
            },
          }}
          pageSizeOptions={[30, 50]}
          disableSelectionOnClick
          rowHeight={40}
          hideFooter
          autoHeight
        />
      </MaterialOrderGridGroup>
    );
  };

  // fetch order
  React.useEffect(() => {
    store.dispatch(getOrder(orderId)).then(() => setIsLoading(false));
  }, [orderId]);

  React.useEffect(() => {
    loadShippedProductions();
  }, [loadShippedProductions]);

  React.useEffect(() => {
    if (shipment) {
      setComment(shipment.comment);
      setOnBackorder(Boolean(shipment.onBackorder));
      // Keep deliveryDate state in sync when shipment changes
      if (shipment.deliveryDate) {
        setDeliveryDate(shipment.deliveryDate.split("T")[0]);
      }
      // Sync editable shipping info
      setShippingInfo({
        preparationDate: shipment?.preparationDate?.split("T")[0] || "",
        sendingDate: shipment?.sendingDate?.split("T")[0] || "",
        carrierOrderDate: shipment?.carrierOrderDate?.split("T")[0] || "",
        carrier: shipment?.carrier || "",
        packingSlip: shipment?.packingSlip || "",
      });
    }
  }, [shipment]);

  // Handlers to edit number of packages and per-package fields
  const handleNumPackagesChange = (value) => {
    const n = Math.max(0, parseInt(value) || 0);
    setPackages((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      if (n > list.length) {
        for (let i = list.length; i < n; i++) {
          list.push({ length: 0, height: 0, width: 0, volumetricWeight: 0 });
        }
      } else if (n < list.length) {
        list.length = n;
      }
      return list;
    });
  };

  const handlePackageFieldChange = (index, field, value) => {
    setPackages((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      list[index] = {
        ...(list[index] || {
          length: 0,
          height: 0,
          width: 0,
          volumetricWeight: 0,
        }),
        [field]: value === "" ? "" : Number(value),
      };
      return list;
    });
  };

  // ─── Loading State ───────────────────────────────────────────────────────────
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        <div style={{ width: "100%", height: "auto" }}>
          <OrderPageHeader
            title={`Order ${order?.orderNumber || ""}`}
            onBack={handleBackToOrder}
          />
          {/* ─── Header: Order Summary ────────────────────────────────────────── */}
          <OrderTable
            key={orderRefreshKey}
            order={order}
            studyCode={studyCode}
            centerNumber={centerNumber}
          />

          {/* ─── Actions: Print Buttons ──────────────────────────────────────── */}
          <Grid container justifyContent="space-around">
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                printCdlTracking(order, shipment, study, centerNumber);
              }}
              style={{ padding: "16px 32px", margin: "16px" }}>
              {t("cdlTracking")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handlePrintAorForm}
              style={{ padding: "16px 32px", margin: "16px" }}>
              {t("aorForm")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                printPackingList(shipment, study, order, centerNumber);
              }}
              style={{ padding: "16px 32px", margin: "16px" }}>
              {t("packingList")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                printShippingLabel(order, shipment);
              }}
              style={{ padding: "16px 32px", margin: "16px" }}>
              {t("shippingLabel")}
            </Button>
          </Grid>
          {/* ─── Shipping Address (Read-only + Edit Dialog) ──────────────────── */}
          <ReadOnlyTextField
            title={t("shippingAddress").toUpperCase()}
            value={fullAddress}
            multiline
            maxRows={4}
            onDoubleClick={cdlUser ? handleDoubleClickAddress : null}
          />
          <EditShippingAddressDialog
            open={dialogOpen}
            handleClose={handleClose}
            hospital={hospital}
            setHospital={setHospital}
            ward={ward}
            setWard={setWard}
            building={building}
            setBuilding={setBuilding}
            street={street}
            setStreet={setStreet}
            floor={floor}
            setFloor={setFloor}
            zipcode={zipcode}
            setZipcode={setZipcode}
            shippingAddress={shippingAddress}
            setShippingAddress={setShippingAddress}
            errorMessage={errorMessage}
            handleSave={handleSaveAddress}
          />

          {/* ─── Materials (Order Content) ───────────────────────────────────── */}
          <Box sx={{ p: 2 }}>
            <Box sx={materialOrderGridListSx}>
              <TextField
                value={t("materialOrder").toUpperCase()}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: { textAlign: "center" },
                }}
                fullWidth
              />{" "}
              <div id="packing-list">
                <CustomDataGrid
                  rows={materialsTypeMaterial}
                  headerName={t("material")}
                />
                <CustomDataGrid
                  rows={materialsTypeSupply}
                  headerName={t("supply")}
                />
                <CustomDataGrid
                  rows={materialsTypeReagent}
                  headerName={t("reagent")}
                />
                <CustomDataGrid
                  rows={materialsTypeDocuments}
                  headerName={t("document")}
                />{" "}
              </div>
            </Box>
          </Box>

          {/* ─── Shipping Information Fields ─────────────────────────────────── */}
          <Box sx={{ p: 2 }}>
            <TextField
              value={t("shippingInformation").toUpperCase()}
              InputProps={{
                readOnly: true,
              }}
              inputProps={{
                style: { textAlign: "center" },
              }}
              fullWidth
            />
            <Grid container spacing={2} style={{ marginTop: "4px" }}>
              {/* preparationDate */}
              <Grid item xs={4}>
                <CustomDatePicker
                  label={t("preparationDate")}
                  name="preparationDate"
                  value={shippingInfo.preparationDate}
                  onChange={(v) =>
                    setShippingInfo((s) => ({ ...s, preparationDate: v }))
                  }
                  maxDate={dayjs()}
                  gridSize={12}
                />
              </Grid>
              {/* sendingDate */}
              <Grid item xs={4}>
                <CustomDatePicker
                  label={t("sendingDate")}
                  name="sendingDate"
                  value={shippingInfo.sendingDate}
                  onChange={(v) =>
                    setShippingInfo((s) => ({ ...s, sendingDate: v }))
                  }
                  maxDate={dayjs()}
                  gridSize={12}
                />
              </Grid>
              {/* carrierOrderDate */}
              <Grid item xs={4}>
                <CustomDatePicker
                  label={t("carrierOrderDate")}
                  name="carrierOrderDate"
                  value={shippingInfo.carrierOrderDate}
                  onChange={(v) =>
                    setShippingInfo((s) => ({ ...s, carrierOrderDate: v }))
                  }
                  maxDate={dayjs()}
                  gridSize={12}
                />
              </Grid>
              {/* carrier */}
              <Grid item xs={4}>
                <TextField
                  label={t("carrier")}
                  name="carrier"
                  fullWidth
                  value={shippingInfo.carrier}
                  onChange={(e) =>
                    setShippingInfo((s) => ({ ...s, carrier: e.target.value }))
                  }
                />
              </Grid>
              {/* airwaybill (packingSlip) */}
              <Grid item xs={4}>
                <TextField
                  label={t("airwaybill")}
                  name="packingSlip"
                  fullWidth
                  value={shippingInfo.packingSlip}
                  onChange={(e) =>
                    setShippingInfo((s) => ({
                      ...s,
                      packingSlip: e.target.value,
                    }))
                  }
                />
              </Grid>
              {/* numberOfPackages - now editable */}
              <Grid item xs={4}>
                <TextField
                  label={t("numberOfPackages")}
                  type="number"
                  name="numPackages"
                  value={packages?.length ?? 0}
                  onChange={(e) => handleNumPackagesChange(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* ─── Package Details (Editable) ─────────────────────────────────── */}
            <Grid
              className="shipping-information"
              container
              spacing={2}
              alignItems="center"
              style={{ marginTop: "8px" }}>
              {Array.from({ length: packages?.length || 0 }, (_, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={3}>
                    <TextField
                      label={`${t("length")} (cm)`}
                      type="number"
                      value={packages[index]?.length ?? 0}
                      onChange={(e) =>
                        handlePackageFieldChange(
                          index,
                          "length",
                          e.target.value,
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">cm</InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label={`${t("height")} (cm)`}
                      type="number"
                      value={packages[index]?.height ?? 0}
                      onChange={(e) =>
                        handlePackageFieldChange(
                          index,
                          "height",
                          e.target.value,
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">cm</InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label={`${t("width")} (cm)`}
                      type="number"
                      value={packages[index]?.width ?? 0}
                      onChange={(e) =>
                        handlePackageFieldChange(index, "width", e.target.value)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">cm</InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label={`${t("volumetricWeight")} (kg)`}
                      type="number"
                      value={packages[index]?.volumetricWeight ?? 0}
                      onChange={(e) =>
                        handlePackageFieldChange(
                          index,
                          "volumetricWeight",
                          e.target.value,
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">kg</InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Box>

          {/* ─── Shipment Comment Editor ─────────────────────────────────────── */}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} md={8}>
                <TextField
                  id="comment"
                  name="comment"
                  label={t("shipmentComment")}
                  multiline
                  rows={3}
                  sx={{
                    height: "100%",
                    "& .MuiInputBase-root": {
                      alignItems: "flex-start",
                      height: "100%",
                    },
                  }}
                  value={comment}
                  onChange={(event) => {
                    setComment(event.target.value);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    height: "100%",
                  }}>
                  <BackorderControl
                    checked={onBackorder}
                    label={t("onBackorder")}
                    onChange={(e) => setOnBackorder(e.target.checked)}
                    sx={{
                      flex: "0 0 56px",
                      "& .MuiTypography-root": {
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      flex: "1 1 56px",
                      minHeight: 56,
                      padding: "16px",
                      whiteSpace: "nowrap",
                    }}
                    onClick={handleUpdateAll}>
                    {t("updateShipping")}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* ─── User Info: Shipping & Reception ─────────────────────────────── */}
            <UserInfo
              title={t("personInChargeOfShipping").toUpperCase()}
              user={shipment?.shippedBy}
              date={dayjs(shipment?.sendingDate).format("DD/MM/YYYY")}
            />

            {shipment?.receivedBy && (
              <UserInfo
                title={t("personInChargeOfReception").toUpperCase()}
                user={shipment?.receivedBy}
                date={dayjs(shipment?.confirmedDeliveryDate).format(
                  "DD/MM/YYYY",
                )}
              />
            )}
          </Box>

          {/* ─── Actions & Delivery Date (two rows) ───────────────────────────── */}
          {/* Row 1: CompleteAor | delivery date picker | Delivery Date Button */}
          <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
            <Grid item xs={4}>
              {/* central datepicker controls the deliveryDate used by the button */}
              <CustomDatePicker
                label={t("deliveryDate")}
                name="deliveryDate"
                value={deliveryDate}
                onChange={setDeliveryDate}
                maxDate={dayjs()}
                gridSize={12}
                gridSx={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={4}>
              {shipment?.deliveryDate ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSave("deliveryDate", deliveryDate)}
                  fullWidth
                  sx={{ padding: "16px 8px", height: "fit-content" }}>
                  {t("updateDeliveryDate")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={confirmDelivery}
                  fullWidth
                  sx={{ padding: "16px 8px", height: "fit-content" }}>
                  {t("confirmDelivery")}
                </Button>
              )}
            </Grid>

            <Grid item xs={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={shipment?.confirmedDeliveryDate ? seeAOR : handleAOR}
                fullWidth
                sx={{ padding: "16px 8px", height: "fit-content" }}>
                {shipment?.confirmedDeliveryDate
                  ? t("seeAor")
                  : t("completeAor")}
              </Button>
            </Grid>
          </Grid>

          {/* Row 2: shipmentLost (left) and backToOrder (right) spaced apart */}
          <Grid
            container
            spacing={2}
            sx={{ mt: 0.1, mb: 2, px: 2 }}
            alignItems="center"
            justifyContent="space-between">
            <Grid item xs={4}>
              {shipment?.status === "shipped" && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLostShipment}
                  fullWidth
                  sx={{ padding: "16px 8px" }}>
                  {t("shipmentLost")}
                </Button>
              )}
            </Grid>

            <Grid item xs={4} />

            <Grid item xs={4}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleBackToOrder}
                fullWidth
                sx={{ padding: "16px 8px" }}>
                {t("backToOrder")}
              </Button>
            </Grid>
          </Grid>
          {/* end Actions & Delivery Date */}

          {/* Inline Edit Dialog */}
          <EditFieldDialog
            open={open}
            onClose={() => setOpen(false)}
            field={currentField}
            label={currentLabel}
            value={currentValue}
            onSave={handleSave}
          />
        </div>
      </Paper>
    </Grid>
  );
};
